import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/Badges";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { goldBtn, goldBtnSm, glassBtn, inputCls, textareaCls, labelCls, AUM, ORG_TYPES, TIERS, SECTORS } from "@/lib/constants";

function Section({ title, children, right }) {
  return (
    <GlassCard className="p-6 sm:p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="pdg-serif text-2xl text-foreground">{title}</h2>
        {right}
      </div>
      {children}
    </GlassCard>
  );
}

export default function Account() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [kyc, setKyc] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [ndas, setNdas] = useState([]);
  const [prefs, setPrefs] = useState({ deal_alerts: true, weekly_digest: true, admin_messages: true });
  const [consent, setConsent] = useState({ aurelia: false, steward: false });
  const [mandateForm, setMandateForm] = useState({ title: "", sectors: [], geographies: "", ticket_size_min: "", ticket_size_max: "", description: "" });

  const load = useCallback(async () => {
    const [p, k, m, n] = await Promise.all([
      api.get("/account"), api.get("/account/kyc"), api.get("/account/mandates"), api.get("/account/nda-history"),
    ]);
    setProfile(p.data);
    setPrefs(p.data.notification_preferences || { deal_alerts: true, weekly_digest: true, admin_messages: true });
    setConsent(p.data.cross_platform_consent || { aurelia: false, steward: false });
    setKyc(k.data); setMandates(m.data); setNdas(n.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const savePrefs = async (next, nextConsent) => {
    await api.put("/account", { notification_preferences: next || prefs, cross_platform_consent: nextConsent || consent });
    toast.success("Preferences saved."); refresh();
  };
  const togglePref = (k) => { const next = { ...prefs, [k]: !prefs[k] }; setPrefs(next); savePrefs(next, null); };
  const toggleConsent = (k) => { const next = { ...consent, [k]: !consent[k] }; setConsent(next); savePrefs(null, next); };

  const uploadKyc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("doc_type", "identity");
    try { await api.post("/account/kyc", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document submitted for review."); load(); refresh(); }
    catch (err) { toast.error("Upload failed"); }
  };

  const submitMandate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/account/mandate", {
        title: mandateForm.title, sectors: mandateForm.sectors,
        geographies: mandateForm.geographies.split(",").map((s) => s.trim()).filter(Boolean),
        ticket_size_min: mandateForm.ticket_size_min, ticket_size_max: mandateForm.ticket_size_max,
        description: mandateForm.description,
      });
      toast.success("Mandate submitted.");
      setMandateForm({ title: "", sectors: [], geographies: "", ticket_size_min: "", ticket_size_max: "", description: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const toggleSector = (s) => setMandateForm((f) => ({ ...f, sectors: f.sectors.includes(s) ? f.sectors.filter((x) => x !== s) : [...f.sectors, s] }));

  if (!profile) return <PortalLayout title="Account"><p className="text-sm text-muted-foreground">Loading…</p></PortalLayout>;

  return (
    <PortalLayout title="Account">
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Profile" right={<StatusBadge status={profile.status} />}>
          <dl className="space-y-3 text-sm">
            {[["Name", profile.full_name], ["Organisation", profile.organisation_name],
              ["Type", ORG_TYPES[profile.organisation_type]], ["Role", profile.role_title],
              ["Email", profile.email], ["Country", profile.country_of_incorporation],
              ["AUM", AUM[profile.aum_range]], ["Tier", TIERS[profile.tier]]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 border-b border-border pb-2">
                <dt className="text-muted-foreground">{k}</dt><dd className="text-foreground text-right">{v || "—"}</dd>
              </div>
            ))}
          </dl>
        </Section>

        <Section title="Notification preferences">
          <div className="space-y-4">
            {[["deal_alerts", "Deal alerts"], ["weekly_digest", "Weekly deal digest"], ["admin_messages", "Desk messages"]].map(([k, label]) => (
              <label key={k} className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-foreground/85">{label}</span>
                <button type="button" onClick={() => togglePref(k)} data-testid={`pref-${k}`}
                  className={`w-11 h-6 rounded-full transition-colors relative ${prefs[k] ? "bg-[hsl(var(--pdg-gold)/0.6)]" : "bg-[hsl(var(--pdg-elevated))]"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-foreground transition-all ${prefs[k] ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </label>
            ))}
          </div>
          <div className="mt-8">
            <div className="pdg-kicker mb-3">Cross-platform data sharing</div>
            <p className="text-xs text-muted-foreground mb-4 leading-6">Explicit consent is required before any data is shared with connected platforms.</p>
            {[["aurelia", "Aurelia (luxury concierge)"], ["steward", "Steward Systems (property management)"]].map(([k, label]) => (
              <label key={k} className="flex items-center justify-between cursor-pointer mb-3">
                <span className="text-sm text-foreground/85">{label}</span>
                <button type="button" onClick={() => toggleConsent(k)} data-testid={`consent-${k}`}
                  className={`w-11 h-6 rounded-full transition-colors relative ${consent[k] ? "bg-[hsl(var(--pdg-gold)/0.6)]" : "bg-[hsl(var(--pdg-elevated))]"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-foreground transition-all ${consent[k] ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </label>
            ))}
          </div>
        </Section>

        <Section title="KYC documents" right={<StatusBadge status={profile.kyc_status} />}>
          <label className="block border border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-[hsl(var(--pdg-gold)/0.4)] transition-colors">
            <input type="file" className="hidden" onChange={uploadKyc} data-testid="kyc-upload-input" />
            <span className="text-sm text-muted-foreground">Click to upload identity or incorporation documents</span>
          </label>
          <div className="mt-4 space-y-2">
            {kyc.map((d) => (
              <div key={d.id} className="flex items-center justify-between text-sm p-3 rounded-lg border border-border">
                <span className="text-foreground/85">{d.filename}</span><StatusBadge status={d.status} />
              </div>
            ))}
            {kyc.length === 0 && <p className="text-xs text-muted-foreground">No documents uploaded.</p>}
          </div>
        </Section>

        <Section title="NDA history">
          <div className="space-y-2">
            {ndas.map((n) => (
              <div key={n.id} className="flex items-center justify-between text-sm p-3 rounded-lg border border-border">
                <span className="text-foreground/85">{n.deal_title}</span>
                <span className="text-xs text-muted-foreground">{new Date(n.accepted_at).toLocaleDateString()}</span>
              </div>
            ))}
            {ndas.length === 0 && <p className="text-xs text-muted-foreground">No NDAs accepted yet.</p>}
          </div>
        </Section>
      </div>

      <div className="mt-6">
        <Section title="Submit a mandate">
          <form onSubmit={submitMandate} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className={labelCls}>Mandate title</label><input required className={inputCls} value={mandateForm.title} onChange={(e) => setMandateForm({ ...mandateForm, title: e.target.value })} data-testid="mandate-title-input" /></div>
              <div><label className={labelCls}>Geographies (comma separated)</label><input className={inputCls} value={mandateForm.geographies} onChange={(e) => setMandateForm({ ...mandateForm, geographies: e.target.value })} data-testid="mandate-geo-input" /></div>
            </div>
            <div>
              <label className={labelCls}>Sectors</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(SECTORS).map(([k, v]) => (
                  <button type="button" key={k} onClick={() => toggleSector(k)}
                    className={`px-3 h-9 rounded-lg text-xs border transition-colors ${mandateForm.sectors.includes(k) ? "border-[hsl(var(--pdg-gold)/0.5)] text-[hsl(var(--pdg-gold))] bg-[hsl(var(--pdg-gold)/0.08)]" : "border-border text-foreground/70"}`}>{v}</button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className={labelCls}>Ticket size min</label><input className={inputCls} value={mandateForm.ticket_size_min} onChange={(e) => setMandateForm({ ...mandateForm, ticket_size_min: e.target.value })} placeholder="£25m" /></div>
              <div><label className={labelCls}>Ticket size max</label><input className={inputCls} value={mandateForm.ticket_size_max} onChange={(e) => setMandateForm({ ...mandateForm, ticket_size_max: e.target.value })} placeholder="£150m" /></div>
            </div>
            <div><label className={labelCls}>Description</label><textarea rows={3} className={textareaCls} value={mandateForm.description} onChange={(e) => setMandateForm({ ...mandateForm, description: e.target.value })} /></div>
            <button className={goldBtn} data-testid="mandate-submit-button">Submit mandate</button>
          </form>
          <div className="mt-6 space-y-2">
            {mandates.map((m) => (
              <div key={m.id} className="flex items-center justify-between text-sm p-3 rounded-lg border border-border">
                <span className="text-foreground/85">{m.title}</span>
                <span className="text-xs text-muted-foreground">{(m.sectors || []).map((s) => SECTORS[s]).join(", ")}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </PortalLayout>
  );
}
